import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Loader2, Play, X, Zap } from "lucide-react";
import DiceRollStage from "./DiceRollStage";
import BondDicePickSelector from "./BondDicePickSelector";
import BondCoinFairPanel from "../bond-coin/BondCoinFairPanel";
import {
  bondDiceBuy,
  bondDiceCancel,
  bondDiceRoll,
  clearBondDiceLastSettled,
  clearBondDicePendingRound,
  fetchBondDiceConstants,
  fetchBondDiceLanding,
  selectBondDiceActionLoading,
  selectBondDiceConstants,
  selectBondDiceLastSettled,
  selectBondDicePendingRound,
} from "../../redux/bondDiceSlice";
import { patchAppUser } from "../../redux/appAuthSlice";
import {
  computeBondDiceQuote,
  formatBondDiceCoin,
  faceLabel,
  formatOddsMultiplier,
  oddsForPick,
  pickLabel,
  resolveBondDiceSettledRound,
  resolvePlayerPick,
} from "../../utils/bondDiceHelpers";
import { usePendingPlayCountdown } from "../../hooks/usePendingPlayCountdown";
import useBondDiceResultDwell from "../../hooks/useBondDiceResultDwell";
import useRevealAnimation from "../../hooks/useRevealAnimation";
import BondPlayReservedTicket from "../bond-play/BondPlayReservedTicket";

const BondDiceStage = ({ isAuthenticated, userCoin, onSignIn, onNotify }) => {
  const dispatch = useDispatch();
  const constants = useSelector(selectBondDiceConstants);
  const pendingRound = useSelector(selectBondDicePendingRound);
  const lastSettled = useSelector(selectBondDiceLastSettled);
  const actionLoading = useSelector(selectBondDiceActionLoading);

  const [playerPick, setPlayerPick] = useState("1");
  const [stakeInput, setStakeInput] = useState("500");
  const [revealTicket, setRevealTicket] = useState(null);
  const {
    isRevealing: rollSpinning,
    revealedRound,
    resetReveal,
    finishReveal,
    runReveal,
  } = useRevealAnimation();

  const stake = useMemo(() => Math.floor(Number(stakeInput) || 0), [stakeInput]);
  const minStake = constants?.minStake || 100;
  const maxStake = constants?.maxStake || 500000;
  const playTtlMs = constants?.pendingPlayTtlMs || 60 * 1000;
  const hasPending = pendingRound?.status === "Pending";
  const visualPendingRound = hasPending ? pendingRound : rollSpinning ? revealTicket : null;
  const showPending = !!visualPendingRound;

  const handleTicketExpired = useCallback(() => {
    resetReveal();
    setRevealTicket(null);
    const stake = Number(pendingRound?.stake || 0);
    dispatch(clearBondDicePendingRound());
    dispatch(fetchBondDiceLanding());
    if (stake > 0) {
      dispatch(patchAppUser({ COIN: Math.max(0, userCoin + stake) }));
    }
    onNotify?.({
      variant: "refund",
      game: "dice",
      title: "Ticket expired",
      subtitle: "Your stake was returned to your balance.",
      amount: stake,
      autoCloseMs: 3500,
    });
  }, [dispatch, onNotify, pendingRound?.stake, resetReveal, userCoin]);

  const { secondsLeft, expired: ticketExpired } = usePendingPlayCountdown({
    active: showPending,
    playExpiresAt: visualPendingRound?.playExpiresAt,
    createdAt: visualPendingRound?.createdAt,
    ttlMs: playTtlMs,
    onExpired: handleTicketExpired,
  });

  useEffect(() => {
    if (!constants) dispatch(fetchBondDiceConstants());
  }, [constants, dispatch]);

  const ticketRound = revealTicket || pendingRound;

  const resolvedRound = useMemo(
    () =>
      resolveBondDiceSettledRound({
        revealedRound,
        lastSettled,
        ticketRound,
      }),
    [revealedRound, lastSettled, ticketRound]
  );

  useEffect(() => {
    if (rollSpinning && resolvedRound) finishReveal(resolvedRound);
  }, [rollSpinning, resolvedRound, finishReveal]);

  useEffect(() => {
    if (showPending) {
      const pendingPick = resolvePlayerPick(visualPendingRound);
      if (pendingPick) setPlayerPick(pendingPick);
    }
  }, [showPending, visualPendingRound]);

  const pickOdds = useMemo(() => oddsForPick(playerPick, constants), [constants, playerPick]);
  const liveQuote = useMemo(
    () => computeBondDiceQuote(stake, playerPick, constants),
    [constants, playerPick, stake]
  );

  const handleResultCleared = useCallback(() => {
    resetReveal();
    dispatch(clearBondDiceLastSettled());
  }, [dispatch, resetReveal]);

  const displaySettledRound = useBondDiceResultDwell(resolvedRound, { onCleared: handleResultCleared });

  const diceResultRound = displaySettledRound ?? resolvedRound;
  const isDiceRolling = Boolean(ticketRound && !resolvedRound && (rollSpinning || actionLoading));
  const showPendingUi = showPending && !resolvedRound;

  const displayRound = showPendingUi ? visualPendingRound : diceResultRound;
  const displayPick = resolvePlayerPick(displayRound);

  const canBuy =
    isAuthenticated &&
    !showPendingUi &&
    !isDiceRolling &&
    stake >= minStake &&
    stake <= maxStake &&
    stake <= userCoin;

  const handleBuy = async () => {
    try {
      await dispatch(bondDiceBuy({ playerPick, stake })).unwrap();
      resetReveal();
      setRevealTicket(null);
      onNotify?.({
        variant: "ticket",
        game: "dice",
        title: "Ticket locked in",
        subtitle: "Your dice roll is reserved — play before the timer expires.",
        pick: pickLabel(playerPick),
        stake,
        maxWin: liveQuote?.maxWin,
        autoCloseMs: 4000,
      });
    } catch (error) {
      onNotify?.(typeof error === "string" ? error : "Could not reserve ticket");
    }
  };

  const handleRoll = async () => {
    if (!pendingRound?._id) return;
    setRevealTicket(pendingRound);
    try {
      const round = await runReveal(() => dispatch(bondDiceRoll({ roundId: pendingRound._id })).unwrap());
      setRevealTicket(null);
      const won = round?.status === "Won";
      const net = Number(round?.playerNetPayout || 0);
      const pick = pickLabel(resolvePlayerPick(round));
      onNotify?.(
        won
          ? {
              variant: "win",
              game: "dice",
              title: "Jackpot roll!",
              subtitle: `${pick} hit on face ${faceLabel(round.outcome)}.`,
              pick,
              outcome: faceLabel(round.outcome),
              stake: Number(round?.stake || pendingRound?.stake || 0),
              netPayout: net,
            }
          : {
              variant: "loss",
              game: "dice",
              title: "Close one",
              subtitle: `Face ${faceLabel(round.outcome)} landed — ${pick} missed.`,
              pick,
              outcome: faceLabel(round.outcome),
              stake: Number(round?.stake || pendingRound?.stake || 0),
            }
      );
    } catch (error) {
      onNotify?.(typeof error === "string" ? error : "Roll failed");
    }
  };

  const handleCancel = async () => {
    if (!pendingRound?._id) return;
    try {
      await dispatch(
        bondDiceCancel({ roundId: pendingRound._id, stake: Number(pendingRound.stake || 0) })
      ).unwrap();
      resetReveal();
      setRevealTicket(null);
      onNotify?.({
        variant: "refund",
        game: "dice",
        title: "Ticket canceled",
        subtitle: "Your stake is back in your wallet.",
        amount: Number(pendingRound.stake || 0),
        autoCloseMs: 3500,
      });
    } catch (error) {
      onNotify?.(typeof error === "string" ? error : "Cancel failed");
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="bcoin-empty">
        <p>Sign in to play Bond Dice.</p>
        <button type="button" className="bcoin-btn bcoin-btn-primary" onClick={onSignIn}>
          Sign in
        </button>
      </div>
    );
  }

  return (
    <div className="bcoin-stage bdice-stage">
      <header className="bcoin-head">
        <div>
          <p className="bcoin-eyebrow">Investor-backed · COIN only · Face {formatOddsMultiplier(constants?.faceOdds || 6)} · Groups {formatOddsMultiplier(constants?.groupOdds || 2)}</p>
          <h1 className="bcoin-title">Bond Dice</h1>
        </div>
        <span className={`bcoin-status ${showPendingUi ? "bcoin-status-pending" : isDiceRolling ? "bcoin-status-pending" : "bcoin-status-muted"}`}>
          {showPendingUi ? "Reserved" : isDiceRolling ? "Rolling" : "Ready"}
        </span>
      </header>

      <DiceRollStage
        outcome={diceResultRound?.outcome ?? null}
        spinning={isDiceRolling}
        locked={showPendingUi && !isDiceRolling}
      />

      {diceResultRound?.outcome != null ? (
        <p className="bcoin-round-outcome">
          Rolled <strong>{faceLabel(displayRound.outcome)}</strong>
          {displayPick ? (
            <>
              {" "}
              · Your pick: {pickLabel(displayPick)}
              {displayRound.status === "Won" ? (
                <> · Won {formatBondDiceCoin(displayRound.playerNetPayout || 0)} COIN</>
              ) : null}
            </>
          ) : null}
        </p>
      ) : (
        <p className="bcoin-round-outcome bcoin-round-outcome-placeholder" aria-hidden>
          &nbsp;
        </p>
      )}

      {(diceResultRound?.seedHash || visualPendingRound?.seedHash) ? (
        <BondCoinFairPanel round={diceResultRound || visualPendingRound} game="dice" compact />
      ) : null}

      {showPendingUi ? (
        <BondPlayReservedTicket
          variant="dice"
          actionVerb="Roll"
          pickLabel={pickLabel(resolvePlayerPick(visualPendingRound))}
          stakeLabel={`${formatBondDiceCoin(visualPendingRound?.stake)} COIN`}
          maxWinLabel={`${formatBondDiceCoin(visualPendingRound?.maxPotentialPayout)} COIN`}
          playExpiresAt={visualPendingRound?.playExpiresAt}
          createdAt={visualPendingRound?.createdAt}
          ttlMs={playTtlMs}
          secondsLeft={secondsLeft}
          expired={ticketExpired}
        />
      ) : (
        <>
          <BondDicePickSelector value={playerPick} onChange={setPlayerPick} constants={constants} />

          <div className="bcoin-join-row bcoin-pool-join-row">
            <label className="bcoin-stake-field">
              Stake (min {formatBondDiceCoin(minStake)})
              <input
                type="number"
                min={minStake}
                max={maxStake}
                step={50}
                value={stakeInput}
                onChange={(event) => setStakeInput(event.target.value)}
              />
            </label>
          </div>

          {liveQuote ? (
            <p className="bcoin-share-preview">
              {formatOddsMultiplier(liveQuote.odds || pickOdds)} · Max win {formatBondDiceCoin(liveQuote.maxWin)} COIN · Pool
              reserve {formatBondDiceCoin(liveQuote.reserve)} COIN
            </p>
          ) : null}
        </>
      )}

      <div className="bcoin-flip-actions">
        {showPendingUi ? (
          <>
            <button type="button" className="bcoin-btn" disabled={actionLoading || isDiceRolling} onClick={handleCancel}>
              <X size={14} />
              Cancel
            </button>
            <button
              type="button"
              className="bcoin-btn bcoin-btn-primary"
              disabled={actionLoading || isDiceRolling || ticketExpired}
              onClick={handleRoll}
            >
              {isDiceRolling ? <Loader2 size={14} className="bcoin-spin" /> : <Play size={14} />}
              {isDiceRolling ? "Rolling…" : "Roll now"}
            </button>
          </>
        ) : (
          <button
            type="button"
            className="bcoin-btn bcoin-btn-primary bcoin-flip-buy"
            disabled={actionLoading || !canBuy}
            onClick={handleBuy}
          >
            {actionLoading ? <Loader2 size={14} className="bcoin-spin" /> : <Zap size={14} />}
            <span className="bcoin-reserve-copy">
              <span>{actionLoading ? "Reserving…" : "Reserve ticket"}</span>
              {liveQuote?.maxWin && !actionLoading ? (
                <span className="bcoin-reserve-potential">
                  Win up to {formatBondDiceCoin(liveQuote.maxWin)} COIN
                </span>
              ) : null}
            </span>
          </button>
        )}
      </div>
    </div>
  );
};

export default BondDiceStage;
